import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { withTranslation, WithTranslation } from 'react-i18next';

interface Props extends WithTranslation {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Last resort when a lazily loaded page or panel cannot be rendered.
 *
 * Without it a failed chunk leaves an empty Suspense boundary: the user sees a blank area
 * and has to guess that refreshing fixes it. Here they get the reason and a reload button.
 */
class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Failed to render a lazily loaded view', error, info.componentStack);
  }

  render() {
    const { children, t } = this.props;
    if (!this.state.hasError) {
      return children;
    }

    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Stack spacing={2} alignItems="center">
          <Typography sx={{ fontWeight: 600 }}>{t('CHUNK_ERROR.TITLE')}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 420 }}>
            {t('CHUNK_ERROR.MESSAGE')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
          >
            {t('CHUNK_ERROR.RELOAD')}
          </Button>
        </Stack>
      </Box>
    );
  }
}

export default withTranslation('components')(ChunkErrorBoundary);
